import React from "react";
import { ABOUT, COMPANY, IMAGES } from "../constants/config";
import { Reveal, StaggerGroup, StaggerItem } from "./Reveal";

// Small inline icon set for the quick-feature pillars — kept as SVG so they
// stay crisp at any size and inherit color via currentColor, no extra assets.
function ShieldIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M12 3l7 3v5c0 4.5-3 8.25-7 9.5-4-1.25-7-5-7-9.5V6l7-3z" />
      <path d="M9.5 12l1.75 1.75L15 10" />
    </svg>
  );
}

function TeamIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="8.5" r="2.5" />
      <path d="M15 10.5c2.6.4 4.5 2.1 4.5 4.5" />
    </svg>
  );
}

function GearIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.2M12 18.8V21M21 12h-2.2M5.2 12H3M18.1 5.9l-1.6 1.6M7.5 16.5l-1.6 1.6M18.1 18.1l-1.6-1.6M7.5 7.5 5.9 5.9" />
    </svg>
  );
}

const PILLARS = [
  { icon: ShieldIcon, accent: "bg-indigo-100 text-indigo-600" },
  { icon: TeamIcon, accent: "bg-indigo-100 text-indigo-600" },
  { icon: GearIcon, accent: "bg-indigo-100 text-indigo-600" },
];

const VALUE_ICONS = [IMAGES.iconMission, IMAGES.iconVision, IMAGES.iconTeam];

function AboutUs() {
  const valueItems = [ABOUT.mission, ABOUT.vision, ABOUT.coreValues];

  return (
    <section
      className="about-grid-pattern relative w-full overflow-hidden bg-indigo-50/40 py-12 md:py-20"
      id="aboutus"
      aria-label="About us section"
    >
      <div
        className="pointer-events-none absolute -right-24 -top-16 h-72 w-72 rounded-full bg-indigo-100/60 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-sky-100/50 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex w-full max-w-[1800px] flex-col px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20">
        <div className="grid w-full grid-cols-1 items-center gap-10 xl:grid-cols-2 xl:gap-14">
          {/* Left: intro copy + quick-feature pillars */}
          <Reveal
            variant="slideRight"
            className="flex w-full flex-col items-center gap-6 text-center xl:items-start xl:text-left"
          >
            <div className="text-[1.15rem] font-semibold uppercase tracking-[0.2em] text-indigo-600 xl:text-[1.35rem]">
              {ABOUT.eyebrow}
            </div>

            <h2 className="text-[1.575rem] font-extrabold leading-[1.25] tracking-tight text-indigo-950 sm:text-[1.8rem] md:text-[2.25rem] xl:text-[2.475rem] 2xl:text-[2.8125rem]">
              <span className="block sm:whitespace-nowrap">{ABOUT.title}</span>
              {ABOUT.titleAccentLead}{" "}
              <span className="text-indigo-600">
                {ABOUT.titleAccentHighlight}
              </span>
              <span className="text-indigo-600">.</span>
            </h2>

            <p className="w-full text-[0.72rem] leading-relaxed text-gray-500 sm:text-[0.81rem] xl:text-[1.0125rem]">
              {ABOUT.description}
            </p>

            <StaggerGroup className="grid w-full grid-cols-1 gap-5 pt-2 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
              {ABOUT.pillars.map((pillar, index) => {
                const Icon = PILLARS[index % PILLARS.length].icon;
                const accent = PILLARS[index % PILLARS.length].accent;
                return (
                  <StaggerItem
                    key={pillar.title}
                    index={index}
                    className="flex items-start gap-3 text-left"
                  >
                    <span
                      className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl ${accent}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-indigo-950 sm:text-base">
                        {pillar.title}
                      </h3>
                      <p className="mt-1 text-xs leading-relaxed text-gray-500 sm:text-sm">
                        {pillar.description}
                      </p>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </Reveal>

          {/* Right: photo with established-since badge */}
          <Reveal variant="slideLeft" index={1}>
            <div className="relative mx-auto w-full max-w-full pb-10 xl:max-w-xl xl:pb-14">
              <div className="hover-zoom aspect-video w-full overflow-hidden rounded-2xl shadow-xl ring-1 ring-indigo-100 xl:aspect-[4/3]">
                <img
                  src={IMAGES.aboutUs}
                  alt="TowerEdge team building project"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="absolute inset-x-4 -bottom-2 flex flex-col gap-3 rounded-2xl bg-indigo-950 p-4 text-white shadow-xl sm:inset-x-6 sm:flex-row sm:items-center sm:gap-5 sm:p-5 xl:inset-x-8">
                <div className="flex flex-shrink-0 flex-col items-center text-center sm:border-r sm:border-white/15 sm:pr-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-indigo-300 sm:text-sm">
                    {ABOUT.establishedBadge.label}
                  </p>
                  <p className="text-2xl font-extrabold leading-tight sm:text-3xl">
                    {ABOUT.establishedBadge.year}
                  </p>
                </div>
                <p className="text-sm leading-relaxed text-indigo-100 sm:text-base">
                  {ABOUT.establishedBadge.description}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Affiliation strip */}
        <Reveal
          variant="slideUp"
          className="mt-4 flex w-full flex-col items-center justify-center gap-3 rounded-2xl border border-indigo-100 bg-white p-5 text-center shadow-md sm:gap-4 sm:p-6 md:mt-8 xl:flex-row xl:flex-wrap"
        >
          <p className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2 text-base leading-relaxed text-gray-600 sm:gap-x-3 sm:text-lg">
            <img
              src={IMAGES.aboutUsWordmark}
              alt={`${COMPANY.name} Engineering Consultants LLP`}
              className="h-8 w-auto flex-shrink-0 object-contain sm:h-10"
            />
            <strong className="flex-shrink-0 whitespace-nowrap font-bold text-[#080717]">
              is associated with
            </strong>
            <a
              href={COMPANY.affiliation.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${COMPANY.affiliation.name} (opens in a new tab)`}
              className="inline-flex flex-shrink-0"
            >
              <img
                src={IMAGES.ontivityLogo}
                alt={COMPANY.affiliation.name}
                className="h-8 w-auto object-contain sm:h-10"
              />
            </a>
          </p>

          <span
            className="hidden h-10 w-px flex-shrink-0 bg-indigo-100 xl:block"
            aria-hidden="true"
          />

          <p className="text-base leading-relaxed text-gray-600 sm:whitespace-nowrap sm:text-lg">
            a leading U.S based telecommunications infrastructure company.
          </p>
        </Reveal>

        {/* Mission / Vision / Values */}
        <StaggerGroup className="mt-4 overflow-hidden rounded-2xl border border-indigo-100 bg-indigo-100 shadow-md md:mt-6">
          {/* 1px grid gap + colored background = hairline dividers that stay
              correct no matter how the columns wrap at each breakpoint. */}
          <div className="grid grid-cols-1 gap-px sm:grid-cols-2 xl:grid-cols-3">
            {valueItems.map((item, index) => (
              <StaggerItem
                key={item.eyebrow}
                index={index}
                className={`flex items-start gap-4 bg-white p-6 text-left sm:p-8 ${
                  index === valueItems.length - 1
                    ? "sm:col-span-2 xl:col-span-1"
                    : ""
                }`}
              >
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-indigo-100 ring-1 ring-indigo-200">
                  <img
                    loading="lazy"
                    src={VALUE_ICONS[index % VALUE_ICONS.length]}
                    alt=""
                    className="h-6 w-6 object-contain"
                  />
                </span>
                <div>
                  <h3 className="mt-1 text-lg font-bold text-indigo-950 md:text-xl">
                    {item.eyebrow}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-500 md:text-base">
                    {item.description}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </div>
        </StaggerGroup>
      </div>
    </section>
  );
}

export default AboutUs;
